// src/services/sensorTypesService.js - Aggiornato con mappatura campi migliorata
import { toItalianTime } from '../utils/timeUtils';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

/**
 * Determina il tipo di sensore in base al nome
 * @param {string} sensorName - Nome del sensore
 * @returns {string} Tipo di sensore (weather, level, regular)
 */
export const getSensorType = (sensorName) => {
  if (sensorName === 'Sensore5') return 'weather';
  if (sensorName === 'SensoreLivello') return 'level';
  return 'regular';
};

/**
 * Normalizza i nomi dei campi nella risposta dell'API per la stazione meteo
 * @param {object} reading - Una singola lettura dal sensore
 * @returns {object} Lettura con nomi dei campi normalizzati
 */
const normalizeWeatherFields = (reading) => {
  // Mappa dei possibili nomi dei campi dalla risposta dell'API
  const fieldMap = {
    // Temperatura e umidità
    'Temp1': 'temperatura',
    'temperatura': 'temperatura',
    'Umid1': 'umidita',
    'umidita': 'umidita',
    
    // Punto di rugiada (diverse possibili denominazioni dal database)
    'PuntoRugiada': 'dewPoint',
    'Punto Rugiada': 'dewPoint',
    'puntoRugiada': 'dewPoint',
    'dewPoint': 'dewPoint',
    
    // Campi specifici meteo - Incluse entrambe le versioni (con e senza spazi)
    'Light Intensity': 'lightIntensity',
    'light_intensity': 'lightIntensity',
    'lightIntensity': 'lightIntensity',
    'LightIntensity': 'lightIntensity',
    
    'UV Index': 'uvIndex',
    'uv_index': 'uvIndex',
    'uvIndex': 'uvIndex',
    'UVIndex': 'uvIndex',
    
    'Wind Speed': 'windSpeed',
    'wind_speed': 'windSpeed',
    'windSpeed': 'windSpeed',
    'WindSpeed': 'windSpeed',
    
    'Wind Direction': 'windDirection',
    'wind_direction': 'windDirection',
    'windDirection': 'windDirection',
    'WindDirection': 'windDirection',
    
    'Rain Gauge': 'rainGauge',
    'rain_gauge': 'rainGauge',
    'rainGauge': 'rainGauge',
    'RainGauge': 'rainGauge',
    
    'Barometric pressure': 'pressure',
    'barometric_pressure': 'pressure',
    'pressure': 'pressure',
    'Barometricpressure': 'pressure'
  };
  
  // Crea un nuovo oggetto con i campi normalizzati
  const normalized = { ...reading };
  
  // Controlla ogni possibile campo nella risposta
  Object.keys(fieldMap).forEach(apiField => {
    if (reading[apiField] !== undefined) {
      const normalizedField = fieldMap[apiField];
      normalized[normalizedField] = reading[apiField];
    }
  });
  
  return normalized;
};

/**
 * Elabora i dati grezzi del sensore in base al tipo
 * @param {string} sensorName - Nome del sensore
 * @param {Array} readings - Letture grezze dal sensore
 * @returns {Array} Letture elaborate
 */
export const processSensorReadings = (sensorName, readings) => {
  if (!readings || !Array.isArray(readings) || readings.length === 0) {
    return [];
  }
  
  const sensorType = getSensorType(sensorName);
  
  return readings.map(reading => {
    // Converti la data in ora italiana
    const date = toItalianTime(reading.dateString);
    
    // Base comune per tutti i sensori
    const processedReading = {
      ...reading,
      date,
      timestamp: date.getTime(),
      timeValue: format(date, 'HH:mm', { locale: it }),
      dateFormatted: format(date, 'dd/MM/yyyy HH:mm', { locale: it })
    };
    
    // Per il sensore di livello, mantieni il campo livello
    if (sensorType === 'level') {
      return {
        ...processedReading,
        // Assicurati che i campi principali siano definiti per compatibilità
        temperatura: null,
        umidita: null,
        livello: reading.livello
      };
    }
    
    // Per la stazione meteo, normalizza tutti i campi specializzati
    if (sensorType === 'weather') {
      const normalizedReading = normalizeWeatherFields(reading);
      
      return {
        ...processedReading,
        temperatura: normalizedReading.temperatura || 0,
        umidita: normalizedReading.umidita || 0,
        lightIntensity: normalizedReading.lightIntensity || 0,
        uvIndex: normalizedReading.uvIndex || 0,
        windSpeed: normalizedReading.windSpeed || 0,
        windDirection: normalizedReading.windDirection || 0,
        rainGauge: normalizedReading.rainGauge || 0,
        pressure: normalizedReading.pressure || 0,
        dewPoint: normalizedReading.dewPoint || 0
      };
    }
    
    // Per i sensori regolari (temperatura/umidità)
    // Supporto opzionale per il Punto di Rugiada (PuntoRugiada)
    const dewPointValue =
      reading.dewPoint ??
      reading.PuntoRugiada ??
      reading["PuntoRugiada"] ??
      reading.puntoRugiada;

    return {
      ...processedReading,
      temperatura: reading.temperatura ?? reading.Temp1 ?? 0,
      umidita: reading.umidita ?? reading.Umid1 ?? 0,
      // Aggiungi il campo dewPoint solo se effettivamente presente nei dati originali
      ...(dewPointValue !== undefined && dewPointValue !== null
        ? { dewPoint: dewPointValue }
        : {})
    };
  });
};